import { useId, useMemo, useRef, useState } from "react";
import { ChevronsUpDown } from "lucide-react";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Other words that find it, such as a currency code. */
  keywords?: string;
}

/** Lower case, without accents, so "reunion" finds "Réunion". */
const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * A choice from a long list that you can type to search. Typing filters by label and keywords; arrows move, Enter picks,
 * Escape or leaving the field keeps the current choice.
 */
export function Combobox({ value, options, onChange, disabled, placeholder = "Search", label }: { value: string; options: ComboboxOption[]; onChange: (value: string) => void; disabled?: boolean; placeholder?: string; label?: string }) {
  const id = useId();
  const list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const selected = options.find((option) => option.value === value);
  const matches = useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean);
    if (!words.length) return options;
    return options.filter((option) => { const text = fold(`${option.label} ${option.keywords ?? ""} ${option.value}`); return words.every((word) => text.includes(word)); });
  }, [options, query]);

  const show = (next: number) => {
    setActive(next);
    list.current?.children[next]?.scrollIntoView?.({ block: "nearest" });
  };
  const start = () => {
    if (disabled) return;
    setQuery("");
    setOpen(true);
    const at = Math.max(0, options.findIndex((option) => option.value === value));
    setActive(at);
    requestAnimationFrame(() => list.current?.children[at]?.scrollIntoView?.({ block: "center" }));
  };
  const pick = (option: ComboboxOption | undefined) => {
    if (option) onChange(option.value);
    setOpen(false);
    setQuery("");
  };

  return <span className="bw-combobox" data-open={open || undefined}>
    <input
      role="combobox"
      aria-label={label}
      aria-expanded={open}
      aria-controls={`${id}-list`}
      aria-autocomplete="list"
      aria-activedescendant={open && matches[active] ? `${id}-${active}` : undefined}
      autoComplete="off"
      disabled={disabled}
      placeholder={open ? placeholder : undefined}
      value={open ? query : selected?.label ?? value}
      onFocus={start}
      onClick={() => { if (!open) start(); }}
      onBlur={() => { setOpen(false); setQuery(""); }}
      onChange={(event) => { setQuery(event.target.value); setOpen(true); setActive(0); list.current?.scrollTo?.({ top: 0 }); }}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown") { event.preventDefault(); if (!open) start(); else show(Math.min(active + 1, matches.length - 1)); }
        else if (event.key === "ArrowUp") { event.preventDefault(); show(Math.max(active - 1, 0)); }
        else if (event.key === "Enter" && open) { event.preventDefault(); pick(matches[active]); }
        else if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); setQuery(""); }
      }}
    />
    <ChevronsUpDown className="bw-combobox-icon" size={16} aria-hidden="true" />
    {open && <ul ref={list} id={`${id}-list`} role="listbox" className="bw-combobox-list" onClick={(event) => event.preventDefault()}>
      {matches.length === 0 && <li className="bw-combobox-empty" role="presentation">Nothing matches “{query}”</li>}
      {matches.map((option, index) => <li
        key={option.value}
        id={`${id}-${index}`}
        role="option"
        aria-selected={option.value === value}
        data-active={index === active || undefined}
        onMouseDown={(event) => event.preventDefault()}
        onMouseMove={() => { if (index !== active) setActive(index); }}
        // Inside a Field's <label>, a click would also go on to the input and open the list again.
        onClick={(event) => { event.preventDefault(); pick(option); }}
      >{option.label}</li>)}
    </ul>}
  </span>;
}
